import { t } from '@lingui/core/macro';

import { MultiSelectDropdown } from '@/components/base/select/multi-select-dropdown';

/** Values allowed in the `qual` search param. */
export const PARTICIPANT_QUALIFICATION_URL_VALUES = [
  'all',
  'qualified',
  'unqualified',
] as const;

export type ParticipantQualificationView =
  (typeof PARTICIPANT_QUALIFICATION_URL_VALUES)[number];

// Single-select screening views over the participants list. Qualified is the
// default; Show all and Not qualified are explicit overrides.
export const PARTICIPANT_QUALIFICATION_FILTER_VALUES = [
  'all',
  'qualified',
  'unqualified',
] as const;

export type ParticipantQualificationFilter =
  (typeof PARTICIPANT_QUALIFICATION_FILTER_VALUES)[number];

// `Record<…, string>` enforces a label for every filter value at compile time.
function getQualificationLabels(): Record<
  ParticipantQualificationFilter,
  string
> {
  return {
    all: t`Show all`,
    qualified: t`Qualified`,
    unqualified: t`Not qualified`,
  };
}

interface ParticipantQualificationFilterProps {
  value: ParticipantQualificationFilter;
  onChange: (next: ParticipantQualificationFilter) => void;
}

export function ParticipantQualificationFilter({
  value,
  onChange,
}: ParticipantQualificationFilterProps) {
  const labels = getQualificationLabels();
  const items = PARTICIPANT_QUALIFICATION_FILTER_VALUES.map((id) => ({
    id,
    label: labels[id],
  }));

  return (
    <MultiSelectDropdown<ParticipantQualificationFilter>
      items={items}
      size="xs"
      selectedKeys={new Set([value])}
      // An empty set only happens via the pill's × button; treat that as
      // clearing the filter back to "Show all".
      onSelectionChange={(next) => onChange([...next][0] ?? 'all')}
      selectionMode="single"
      showRadios
      triggerVariant="button"
      placeholder={t`Screening`}
      triggerPrefix={t`Screening`}
      triggerHiddenKeys={['all']}
      noWrap
      matchTriggerWidth={false}
      popoverPlacement="bottom start"
      popoverClassName="min-w-[200px]"
      aria-label={t`Filter by screening`}
    />
  );
}
